import type { PlaylistTrack } from "../api/types";
import { t } from "../i18n";

type MusicIcon = "music" | "previous" | "next" | "play" | "pause" | "volume" | "mute" | "close";

function icon(name: MusicIcon): SVGSVGElement {
  // Inline outline SVGs, not emoji or a font that may be missing on devices.
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "1.85");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("aria-hidden", "true");
  const shapes: Record<MusicIcon, string[]> = {
    music: ["M9 18V5l12-2v13", "M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0", "M21 16a3 3 0 1 1-6 0 3 3 0 0 1 6 0"],
    previous: ["M19 4 9 12l10 8V4", "M5 5v14"],
    next: ["M5 4l10 8-10 8V4", "M19 5v14"],
    play: ["m8 5 11 7-11 7V5Z"],
    pause: ["M9 5v14", "M15 5v14"],
    volume: ["M11 5 6 9H3v6h3l5 4V5", "M15 9a5 5 0 0 1 0 6", "M18 5a10 10 0 0 1 0 14"],
    mute: ["M11 5 6 9H3v6h3l5 4V5", "M16 9l5 6", "M21 9l-5 6"],
    close: ["M18 6 6 18", "M6 6l12 12"],
  };
  for (const d of shapes[name]) {
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", d);
    svg.append(path);
  }
  return svg;
}

function button(className: string, label: string, symbol: MusicIcon | null): HTMLButtonElement {
  const element = document.createElement("button");
  element.type = "button";
  element.className = className;
  element.title = label;
  element.setAttribute("aria-label", label);
  if (symbol) element.replaceChildren(icon(symbol));
  return element;
}

/** Shared animated bars for the toolbar trigger and the player artwork. */
function equalizer(): HTMLDivElement {
  const bars = document.createElement("div");
  bars.className = "city-music-equalizer";
  bars.setAttribute("aria-hidden", "true");
  for (let i = 0; i < 5; i++) bars.append(document.createElement("i"));
  return bars;
}

function clock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const time = Math.floor(seconds);
  return String(Math.floor(time / 60)) + ":" + String(time % 60).padStart(2, "0");
}

/** Single city soundtrack with a responsive mini-player and a quick navbar mute. */
export class CityMusicPlayer {
  readonly root = document.createElement("div");
  private readonly audio = new Audio();
  private readonly tracks: PlaylistTrack[];
  private readonly trigger = button("city-music-trigger", t("Open music player", "باز کردن پخش‌کننده موسیقی"), null);
  private readonly quickMute = button("city-music-quick-mute", t("Mute music", "بی‌صدا کردن موسیقی"), "volume");
  private readonly popover = document.createElement("section");
  private readonly title = document.createElement("strong");
  private readonly artist = document.createElement("span");
  private readonly artwork = document.createElement("div");
  private readonly previous = button("city-music-previous", t("Previous track", "آهنگ قبلی"), "previous");
  private readonly next = button("city-music-next", t("Next track", "آهنگ بعدی"), "next");
  private readonly play = button("city-music-play", t("Play music", "پخش موسیقی"), "play");
  private readonly volumeMute = button("city-music-mute", t("Mute music", "بی‌صدا کردن موسیقی"), "volume");
  private readonly volume = document.createElement("input");
  private readonly volumeText = document.createElement("output");
  private readonly progress = document.createElement("div");
  private readonly progressFill = document.createElement("span");
  private readonly elapsed = document.createElement("span");
  private readonly total = document.createElement("span");
  private readonly abort = new AbortController();
  private index = -1;
  private destroyed = false;
  private lastAudibleVolume = 0.35;
  private pendingTrackIndex: number | null = null;
  private skipTimer: number | null = null;

  constructor(playlist: PlaylistTrack[], initialVolume: number) {
    this.tracks = playlist.filter(track => Boolean(track.url?.trim()));
    this.root.className = "city-music-player";
    this.root.hidden = this.tracks.length === 0;
    if (!this.tracks.length) return;

    this.audio.preload = "metadata";
    this.audio.volume = Number.isFinite(initialVolume)
      ? Math.max(0, Math.min(1, initialVolume)) : 0.35;
    // Preserve the user's last audible level while showing 0% when muted.
    this.lastAudibleVolume = this.audio.volume > 0 ? this.audio.volume : 0.35;

    const navButtons = document.createElement("div");
    navButtons.className = "city-music-nav-buttons";
    this.trigger.replaceChildren(equalizer());
    this.trigger.setAttribute("aria-haspopup", "true");
    this.trigger.setAttribute("aria-controls", "city-music-popover");
    this.trigger.setAttribute("aria-expanded", "false");
    this.trigger.addEventListener("click", () => this.setOpen(this.popover.hidden));
    this.quickMute.addEventListener("click", () => this.toggleMute());
    navButtons.append(this.trigger, this.quickMute);

    this.popover.className = "city-music-popover";
    this.popover.id = "city-music-popover";
    this.popover.hidden = true;
    this.popover.setAttribute("role", "group");
    this.popover.setAttribute("aria-label", t("City music player", "پخش‌کننده موسیقی شهر"));

    const header = document.createElement("header");
    header.className = "city-music-header";
    const close = button("city-music-close", t("Close player", "بستن پخش‌کننده"), "close");
    close.addEventListener("click", () => {
      this.setOpen(false);
      this.trigger.focus();
    });
    header.append(close);

    const media = document.createElement("div");
    media.className = "city-music-media";
    this.artwork.className = "city-music-artwork";
    this.artwork.append(equalizer());
    const details = document.createElement("div");
    details.className = "city-music-details";
    this.title.className = "city-music-track";
    this.title.setAttribute("dir", "auto");
    this.title.setAttribute("aria-live", "polite");
    this.artist.className = "city-music-artist";
    this.artist.setAttribute("dir", "auto");
    details.append(this.title, this.artist);
    media.append(this.artwork, details);

    const timeline = document.createElement("div");
    timeline.className = "city-music-timeline";
    // Passive progress indicator: no seeking, no extra HTTP Range requests from dragging.
    this.progress.className = "city-music-progress-bar";
    this.progress.setAttribute("role", "progressbar");
    this.progress.setAttribute("aria-label", t("Track progress", "پیشرفت آهنگ"));
    this.progress.setAttribute("aria-valuemin", "0");
    this.progress.setAttribute("aria-valuemax", "100");
    this.progress.setAttribute("aria-valuenow", "0");
    this.progressFill.className = "city-music-progress-fill";
    this.progress.append(this.progressFill);
    const timeLabels = document.createElement("div");
    timeLabels.className = "city-music-times";
    timeLabels.setAttribute("dir", "ltr");
    timeLabels.append(this.elapsed, this.total);
    timeline.append(this.progress, timeLabels);

    const transport = document.createElement("div");
    transport.className = "city-music-transport";
    this.previous.addEventListener("click", () => this.queueTrackChange(-1));
    this.next.addEventListener("click", () => this.queueTrackChange(1));
    this.play.addEventListener("click", () => {
      if (this.audio.paused) {
        if (this.index < 0) void this.playIndex(0);
        else void this.resume();
      } else {
        this.audio.pause();
        this.render();
      }
    });
    transport.append(this.previous, this.play, this.next);

    const volumeRow = document.createElement("div");
    volumeRow.className = "city-music-volume";
    this.volumeMute.addEventListener("click", () => this.toggleMute());
    this.volume.type = "range";
    this.volume.id = "city-music-volume-slider";
    this.volume.name = "city_music_volume";
    this.volume.min = "0";
    this.volume.max = "1";
    this.volume.step = "0.01";
    this.volume.value = String(this.audio.volume);
    this.volume.setAttribute("aria-label", t("Music volume", "بلندی صدای موسیقی"));
    this.volume.addEventListener("input", () => {
      const level = Number(this.volume.value);
      this.audio.volume = level;
      // Dragging to zero behaves exactly like mute; moving above zero unmutes.
      this.audio.muted = level === 0;
      if (level > 0) this.lastAudibleVolume = level;
      this.render();
    });
    this.volumeText.setAttribute("aria-hidden", "true");
    volumeRow.append(this.volumeMute, this.volume, this.volumeText);

    this.popover.append(header, media, timeline, transport, volumeRow);
    this.root.append(navButtons, this.popover);

    this.audio.addEventListener("play", () => this.render());
    this.audio.addEventListener("pause", () => this.render());
    this.audio.addEventListener("volumechange", () => this.render());
    this.audio.addEventListener("loadedmetadata", () => this.renderTimeline());
    this.audio.addEventListener("durationchange", () => this.renderTimeline());
    this.audio.addEventListener("timeupdate", () => this.renderTimeline());
    this.audio.addEventListener("ended", () => {
      void this.playIndex((this.index + 1) % this.tracks.length);
    });

    document.addEventListener("pointerdown", event => {
      if (!this.root.contains(event.target as Node)) this.setOpen(false);
    }, { signal: this.abort.signal });
    document.addEventListener("keydown", event => {
      if (event.key === "Escape" && !this.popover.hidden) {
        this.setOpen(false);
        this.trigger.focus();
      }
    }, { signal: this.abort.signal });

    // Preserve existing autoplay behavior where the browser permits it.
    void this.playRandom();
    const unlock = () => {
      if (this.audio.paused && !this.audio.muted) {
        if (this.index < 0) void this.playRandom();
        else void this.resume();
      }
    };
    window.addEventListener("pointerdown", unlock, { once: true, signal: this.abort.signal });
    window.addEventListener("keydown", unlock, { once: true, signal: this.abort.signal });
    this.render();
  }

  private setOpen(open: boolean): void {
    this.popover.hidden = !open;
    this.trigger.setAttribute("aria-expanded", String(open));
  }

  private toggleMute(): void {
    if (this.audio.muted || this.audio.volume === 0) {
      this.audio.volume = this.lastAudibleVolume;
      this.audio.muted = false;
    } else {
      this.lastAudibleVolume = this.audio.volume;
      this.audio.muted = true;
    }
    this.render();
    if (!this.audio.muted && this.audio.paused) {
      if (this.index < 0) void this.playRandom();
      else void this.resume();
    }
  }

  private clearQueuedTrackChange(): void {
    if (this.skipTimer !== null) window.clearTimeout(this.skipTimer);
    this.skipTimer = null;
    this.pendingTrackIndex = null;
  }

  private queueTrackChange(direction: -1 | 1): void {
    if (this.destroyed || !this.tracks.length) return;
    const current = this.pendingTrackIndex ?? this.index;
    const index = current < 0 ? 0 : (current + direction + this.tracks.length) % this.tracks.length;
    this.pendingTrackIndex = index;
    if (this.skipTimer !== null) window.clearTimeout(this.skipTimer);
    // Burst navigation loads only the final track, not every intermediate MP3.
    this.skipTimer = window.setTimeout(() => {
      const queued = this.pendingTrackIndex;
      this.pendingTrackIndex = null;
      this.skipTimer = null;
      if (queued !== null && !this.destroyed) void this.playIndex(queued);
    }, 180);
  }

  private async playRandom(): Promise<void> {
    if (this.destroyed || !this.tracks.length) return;
    let next = Math.floor(Math.random() * this.tracks.length);
    if (this.tracks.length > 1 && next === this.index) next = (next + 1) % this.tracks.length;
    await this.playIndex(next);
  }

  private async playIndex(index: number): Promise<void> {
    if (this.destroyed || !this.tracks.length) return;
    this.index = index;
    const url = this.tracks[index].url;
    if (this.audio.src !== new URL(url, location.href).href) {
      this.audio.pause(); // Cancel streaming of the old file before switching.
      this.audio.src = url;
    }
    this.render();
    await this.resume();
  }

  private async resume(): Promise<void> {
    if (this.destroyed) return;
    try { await this.audio.play(); } catch { /* Autoplay policy or an unavailable file. */ }
    if (!this.destroyed) this.render();
  }

  private renderTimeline(): void {
    const duration = this.audio.duration;
    const seekable = Number.isFinite(duration) && duration > 0;
    const current = Number.isFinite(this.audio.currentTime) ? this.audio.currentTime : 0;
    const percent = seekable ? Math.min(100, Math.max(0, current / duration * 100)) : 0;
    this.progressFill.style.width = percent + "%";
    this.progress.setAttribute("aria-valuenow", String(Math.round(percent)));
    this.elapsed.textContent = clock(current);
    this.total.textContent = seekable ? clock(duration) : "--:--";
  }

  private render(): void {
    const track = this.tracks[this.index];
    this.title.textContent = track?.title || t("City playlist", "پلی‌لیست شهر");
    this.artist.textContent = track?.artist || t("City soundtrack", "موسیقی شهر");
    const paused = this.audio.paused;
    const label = paused ? t("Play music", "پخش موسیقی") : t("Pause music", "مکث موسیقی");
    this.play.replaceChildren(icon(paused ? "play" : "pause"));
    this.play.setAttribute("aria-label", label);
    this.play.title = label;
    const muted = this.audio.muted || this.audio.volume === 0;
    const muteLabel = muted ? t("Unmute music", "وصل کردن صدای موسیقی") : t("Mute music", "بی‌صدا کردن موسیقی");
    for (const target of [this.quickMute, this.volumeMute]) {
      target.replaceChildren(icon(muted ? "mute" : "volume"));
      target.title = muteLabel;
      target.setAttribute("aria-label", muteLabel);
      target.setAttribute("aria-pressed", String(muted));
    }
    this.trigger.classList.toggle("is-playing", !paused);
    this.artwork.classList.toggle("is-playing", !paused);
    this.quickMute.classList.toggle("is-muted", muted);
    const visibleVolume = muted ? 0 : this.audio.volume;
    this.volume.value = String(visibleVolume);
    this.volume.style.setProperty("--city-music-volume", Math.round(visibleVolume * 100) + "%");
    this.volumeText.textContent = Math.round(visibleVolume * 100) + "%";
    this.renderTimeline();
  }

  destroy(): void {
    this.destroyed = true;
    this.clearQueuedTrackChange();
    this.abort.abort();
    this.audio.pause();
    this.audio.removeAttribute("src");
    this.audio.load();
    this.root.remove();
  }
}
